import { NumeralFormat } from '@/modules/core/app/constants/NumeralFormat';
import { extractFloat } from '@/modules/core/app/helpers/NumberHelper';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { BTableColumn } from '/@/modules/core/table/models/BTableColumn';

export class TableColumn extends BTableColumn {
  /**
   * @var {string}
   */
  format;

  /**
   * @var {string}
   */
  currencySymbol;

  /**
   * @var {number}
   */
  precision;

  /**
   * @var {number}
   */
  order;

  /**
   * @var {boolean}
   */
  filterable;

  /**
   * @var {boolean}
   */
  renderHtml;

  /**
   * @var {boolean}
   */
  isPrimaryKey;

  /**
   * @var {boolean}
   */
  isPrimaryName;

  /**
   * @var {boolean}
   */
  defaultSort;

  /**
   * @var {boolean}
   */
  invertSortDirection;

  /**
   * @var {boolean}
   */
  isCustomCellRender;

  /**
   * @var {Object}
   */
  values;

  /**
   * @var {?array<TableColumn>}
   */
  extended;

  /**
   * @var {function}
   */
  customRenderFn;

  /**
   * @var {?CustomTableFilter}
   */
  customTableFilter;

  /**
   * @var {?string}
   */
  filterParam;

  /**
   * @var {boolean}
   */
  isCustomHeaderRender;

  /**
   * @var {?HeaderTooltipConfig}
   */
  headerTooltipConfig;

  constructor(column = {}) {
    super(column);

    this.format = column.format || ColumnFormat.FORMAT_STRING;
    this.currencySymbol = column.currencySymbol || null;
    this.precision = column.precision;
    this.order = column.order;
    this.visible = column.visible ?? true;
    this.filterable = column.filterable || false;
    this.renderHtml = column.renderHtml || false;
    this.isPrimaryKey = column.isPrimaryKey;
    this.isPrimaryName = column.isPrimaryName;
    this.defaultSort = column.defaultSort || false;
    this.invertSortDirection = column.invertSortDirection || false;
    this.isCustomCellRender = column.isCustomCellRender || false;
    this.values = column.values || null;
    this.extended = column.extended || null;
    this.customRenderFn = column.customRenderFn || null;
    this.customTableFilter = column.customTableFilter || null;
    this.filterParam = column.filterParam || column.field;
    this.isCustomHeaderRender = column.isCustomHeaderRender || false;
    this.headerTooltipConfig = column.headerTooltipConfig || null;
    this.searchField = column.searchField || column.field;
    this.customSort = column.customSort || this.defaultCustomSort() || undefined;
  }

  defaultCustomSort = () => {
    switch (this.format) {
      case ColumnFormat.FORMAT_CURRENCY:
        return this.currencySort;
      case ColumnFormat.FORMAT_STRING:
        return this.stringSort;
    }
  };

  currencySort = (a, b, isAsc) => {
    const aDisplayValue = this.customRenderFn?.(a) || a[this.field];
    const bDisplayValue = this.customRenderFn?.(b) || b[this.field];

    if (aDisplayValue === NumeralFormat.NULL && bDisplayValue === NumeralFormat.NULL) {
      return 0;
    }
    if (aDisplayValue === NumeralFormat.NULL) {
      return 1;
    }
    if (bDisplayValue === NumeralFormat.NULL) {
      return -1;
    }

    const aValue = extractFloat(aDisplayValue);
    const bValue = extractFloat(bDisplayValue);

    if (aValue < bValue) {
      return isAsc ? -1 : 1;
    }
    if (aValue > bValue) {
      return isAsc ? 1 : -1;
    }
    return 0;
  };

  /**
   * Case-insensitive alphabetical sort
   */
  stringSort = (a, b, isAsc) => {
    const aValue = (this.customRenderFn?.(a) || a[this.field]).toLowerCase();
    const bValue = (this.customRenderFn?.(b) || b[this.field]).toLowerCase();

    if (aValue < bValue) {
      return isAsc ? -1 : 1;
    }
    if (aValue > bValue) {
      return isAsc ? 1 : -1;
    }
    return 0;
  };

  /**
   * @returns {string}
   */
  get footerSlotName() {
    return `${this.field}-footer`;
  }

  /**
   * @return {boolean}
   */
  get isInteger() {
    return (
      this.format === ColumnFormat.FORMAT_INTEGER ||
      this.format === ColumnFormat.FORMAT_DECIMAL ||
      this.format === ColumnFormat.FORMAT_PERCENT ||
      this.format === ColumnFormat.FORMAT_CURRENCY ||
      this.format === ColumnFormat.FORMAT_TIME
    );
  }

  /**
   * @return {boolean}
   */
  get isNumeric() {
    return (
      this.format === ColumnFormat.FORMAT_INTEGER ||
      this.format === ColumnFormat.FORMAT_DECIMAL ||
      this.format === ColumnFormat.FORMAT_PERCENT ||
      this.format === ColumnFormat.FORMAT_CURRENCY
    );
  }

  /**
   * @return {boolean}
   */
  get isTime() {
    return this.format === ColumnFormat.FORMAT_TIME;
  }

  /**
   * @return {boolean}
   */
  get isEnum() {
    return this.format === ColumnFormat.FORMAT_ENUM;
  }

  /**
   * @return {boolean}
   */
  get isDateTime() {
    return this.format === ColumnFormat.FORMAT_DATETIME;
  }

  /**
   * @return {boolean}
   */
  get isDate() {
    return this.format === ColumnFormat.FORMAT_DATE;
  }

  /**
   * @return {boolean}
   */
  get isBoolean() {
    return this.format === ColumnFormat.FORMAT_BOOLEAN;
  }

  /**
   * @return {boolean}
   */
  get isString() {
    return this.format === ColumnFormat.FORMAT_STRING;
  }

  /**
   * @return {boolean}
   */
  get isFilterable() {
    return !!this.filterable;
  }

  /**
   * @return {string}
   */
  get _field() {
    return `_${this.field}`;
  }
}
